/** @format */

import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Stack,
    Typography,
    TextField,
    Chip,
    CircularProgress,
    Divider,
    Alert,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import {
    submitGetSmsStatus,
    submitSearchSmsNumbers,
    submitProvisionSms,
    submitReleaseSms,
    submitUpdateMessagingService,
    submitTestSms,
    ITwilioSmsStatus,
    ITwilioAvailableNumber,
} from "../../redux/services/collectService";
import {
    CollectSectionCard,
    CollectPrimaryButton,
    CollectGhostButton,
    FlexBetween,
} from "./styles";
import { useToast } from "../../providers/ToastProvider";

/**
 * Stage 2F — SMS provisioning panel.
 *
 * Wraps Twilio number search, purchase, release, and a one-off test send.
 * Self-contained: fetches its own status on mount and after every mutating
 * call. Read-only when the caller is not OWNER/ADMIN (we let the server
 * 403 do the enforcement — the UI just disables buttons).
 */
interface Props {
    businessId: string;
    canEdit: boolean;
}

const CollectSmsProvisioning: React.FC<Props> = ({ businessId, canEdit }) => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<ITwilioSmsStatus | null>(null);

    const [country, setCountry] = useState("US");
    const [areaCode, setAreaCode] = useState("");
    const [searching, setSearching] = useState(false);
    const [numbers, setNumbers] = useState<ITwilioAvailableNumber[]>([]);
    const [buyingNumber, setBuyingNumber] = useState<string | null>(null);
    const [releasing, setReleasing] = useState(false);

    const [testTo, setTestTo] = useState("");
    const [testBody, setTestBody] = useState(
        "This is a test from your Collect workspace.",
    );
    const [testing, setTesting] = useState(false);

    const [msgServiceSid, setMsgServiceSid] = useState("");
    const [savingMsgService, setSavingMsgService] = useState(false);

    const loadStatus = useCallback(async () => {
        if (!businessId) return;
        try {
            const s = await submitGetSmsStatus(businessId);
            setStatus(s);
            setMsgServiceSid(s?.twilio?.messagingServiceSid || "");
        } catch (err: any) {
            toast.error(err?.message || "Failed to load SMS status");
        } finally {
            setLoading(false);
        }
    }, [businessId, toast]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const handleSearch = async () => {
        if (!businessId) return;
        setSearching(true);
        try {
            const found = await submitSearchSmsNumbers(businessId, {
                country: country || "US",
                areaCode: areaCode.trim() || undefined,
                limit: 10,
            });
            setNumbers(found);
            if (found.length === 0) {
                toast.info("No numbers matched those filters");
            }
        } catch (err: any) {
            toast.error(err?.message || "Search failed");
        } finally {
            setSearching(false);
        }
    };

    const handleBuy = async (phoneNumber: string) => {
        if (!businessId) return;
        setBuyingNumber(phoneNumber);
        try {
            await submitProvisionSms(businessId, { phoneNumber });
            toast.success(`Provisioned ${phoneNumber}`);
            setNumbers([]);
            await loadStatus();
        } catch (err: any) {
            toast.error(err?.message || "Provision failed");
        } finally {
            setBuyingNumber(null);
        }
    };

    const handleRelease = async () => {
        if (!businessId) return;
        if (
            !window.confirm(
                "Release this Twilio number? Inbound replies to it will stop reaching your workspace.",
            )
        )
            return;
        setReleasing(true);
        try {
            await submitReleaseSms(businessId);
            toast.success("Number released");
            await loadStatus();
        } catch (err: any) {
            toast.error(err?.message || "Release failed");
        } finally {
            setReleasing(false);
        }
    };

    const handleSaveMsgService = async () => {
        if (!businessId) return;
        const sid = msgServiceSid.trim();
        if (sid && !/^MG[0-9a-fA-F]{32}$/.test(sid)) {
            toast.warning("Enter a valid Messaging Service SID (MG…), or clear it.");
            return;
        }
        setSavingMsgService(true);
        try {
            await submitUpdateMessagingService(businessId, sid);
            toast.success(
                sid ? "Messaging Service linked" : "Messaging Service cleared",
            );
            await loadStatus();
        } catch (err: any) {
            toast.error(err?.message || "Failed to update Messaging Service");
        } finally {
            setSavingMsgService(false);
        }
    };

    const handleTest = async () => {
        if (!businessId) return;
        if (!testTo.trim() || !testBody.trim()) {
            toast.warning("Enter a recipient and body first");
            return;
        }
        setTesting(true);
        try {
            const res = await submitTestSms(businessId, {
                to: testTo.trim(),
                body: testBody,
            });
            toast.success(`Sent (sid: ${res.sid.slice(0, 12)}…, status: ${res.status})`);
        } catch (err: any) {
            toast.error(err?.message || "Test send failed");
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <CollectSectionCard sx={{ mb: 3, p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading SMS status…</Typography>
                </Stack>
            </CollectSectionCard>
        );
    }

    const provisioned = !!status?.twilio?.phoneNumber;
    const configured = !!status?.configured;

    return (
        <CollectSectionCard sx={{ mb: 3, p: 3 }}>
            <FlexBetween sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6">SMS (Twilio)</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Provision a dedicated number so reminders can be sent and
                        replies received by text.
                    </Typography>
                </Box>
                <Chip
                    size="small"
                    label={
                        !configured
                            ? "Platform not configured"
                            : provisioned
                                ? "Active"
                                : "Not provisioned"
                    }
                    color={
                        !configured
                            ? "warning"
                            : provisioned
                                ? "success"
                                : "default"
                    }
                />
            </FlexBetween>

            {!configured && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Twilio credentials are not set on the server (TWILIO_ACCOUNT_SID /
                    TWILIO_AUTH_TOKEN). Provisioning is disabled until they are
                    configured.
                </Alert>
            )}

            {provisioned && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status?.twilio?.phoneNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {status?.twilio?.friendlyName || "—"}
                        {status?.twilio?.provisionedAt &&
                            ` · since ${new Date(
                                status.twilio.provisionedAt,
                            ).toLocaleDateString()}`}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                        <CollectGhostButton
                            onClick={handleRelease}
                            disabled={!canEdit || releasing}
                        >
                            {releasing ? "Releasing…" : "Release number"}
                        </CollectGhostButton>
                    </Box>
                </Box>
            )}

            {provisioned && (
                <Box sx={{ mb: 2 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        A2P 10DLC Messaging Service
                    </Typography>
                    {status?.platformMessagingServiceSid ? (
                        <Alert severity="success" sx={{ mb: 1 }}>
                            Your texts already route through the platform A2P
                            campaign automatically — no setup needed here. Only
                            paste a SID below if you need to override the platform
                            default with your own Messaging Service.
                        </Alert>
                    ) : (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                        >
                            Optional. Paste the Messaging Service SID (MG…) that
                            your approved A2P campaign is registered to. Once
                            linked, reminders and test sends route through the
                            campaign so US carriers stop blocking them (error
                            30034). Most workspaces can leave this blank, it is
                            configured platform-wide by your administrator.
                        </Typography>
                    )}
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                            size="small"
                            label="Messaging Service SID (optional override)"
                            placeholder="MG…"
                            value={msgServiceSid}
                            onChange={(e) => setMsgServiceSid(e.target.value.trim())}
                            disabled={!canEdit}
                            fullWidth
                        />
                        <CollectPrimaryButton
                            onClick={handleSaveMsgService}
                            disabled={!canEdit || savingMsgService}
                        >
                            {savingMsgService ? "Saving…" : "Save"}
                        </CollectPrimaryButton>
                    </Stack>
                    {status?.twilio?.messagingServiceSid && (
                        <Typography
                            variant="caption"
                            color="success.main"
                            sx={{ display: "block", mt: 0.5 }}
                        >
                            Override active · sends route through {status.twilio.messagingServiceSid}
                        </Typography>
                    )}
                </Box>
            )}

            {!provisioned && configured && (
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            label="Country"
                            value={country}
                            onChange={(e) => setCountry(e.target.value.toUpperCase())}
                            sx={{ width: 90 }}
                            disabled={!canEdit}
                        />
                        <TextField
                            size="small"
                            label="Area code"
                            value={areaCode}
                            onChange={(e) => setAreaCode(e.target.value)}
                            sx={{ width: 120 }}
                            disabled={!canEdit}
                        />
                        <CollectPrimaryButton
                            onClick={handleSearch}
                            disabled={!canEdit || searching}
                        >
                            {searching ? "Searching…" : "Search"}
                        </CollectPrimaryButton>
                    </Stack>
                    {numbers.length > 0 && (
                        <List
                            dense
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                maxHeight: 260,
                                overflow: "auto",
                            }}
                        >
                            {numbers.map((n) => (
                                <ListItem
                                    key={n.phoneNumber}
                                    secondaryAction={
                                        <CollectPrimaryButton
                                            onClick={() => handleBuy(n.phoneNumber)}
                                            disabled={
                                                !canEdit ||
                                                buyingNumber === n.phoneNumber
                                            }
                                        >
                                            {buyingNumber === n.phoneNumber
                                                ? "Buying…"
                                                : "Buy"}
                                        </CollectPrimaryButton>
                                    }
                                >
                                    <ListItemText
                                        primary={n.phoneNumber}
                                        secondary={[n.locality, n.region]
                                            .filter(Boolean)
                                            .join(", ")}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            )}

            {provisioned && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Send test message
                    </Typography>
                    <Stack spacing={1}>
                        <TextField
                            size="small"
                            label="To (E.164, e.g. +14155551234)"
                            value={testTo}
                            onChange={(e) => setTestTo(e.target.value)}
                            disabled={!canEdit}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Body"
                            value={testBody}
                            onChange={(e) => setTestBody(e.target.value)}
                            multiline
                            minRows={2}
                            disabled={!canEdit}
                            fullWidth
                            helperText={`${testBody.length} / 320 chars`}
                        />
                        <Box>
                            <CollectPrimaryButton
                                onClick={handleTest}
                                disabled={!canEdit || testing}
                            >
                                {testing ? "Sending…" : "Send test"}
                            </CollectPrimaryButton>
                        </Box>
                    </Stack>
                </>
            )}
        </CollectSectionCard>
    );
};

export default CollectSmsProvisioning;
